// ###########################################################################################################

import { IconNames } from "../common/icon/LucideIcon";
import { ResourceName, RouteKey } from "../main/Routing";
import { CustomFieldDataType } from "./CustomFields/CustomFieldsBase";
import { CustomType } from "./CustomTypes/CustomTypes";
import { Person } from "./Person";
import { SignatureType, SignatureSuggestions } from "./Signature";

// Utility types
export type Nullable<T> = { [K in keyof T]: T[K] | null }; // All property values can be null
export type ValueOf<T> = T[keyof T]; // Value Of something
export type IsRequired<T, K extends keyof T> = Exclude<T, K> & { [key in K]-?: Required<T[key]> }; // Make specific properties required
export type RequireKeys<T extends object, K extends keyof T> = Required<Pick<T, K>> & Omit<T, K>; // Make keys required e.g. RequireKeys<Dataset, "id" | "name">
export type DefinedPropertyObject<T extends StringIndexedDict> = { [key in keyof RequireKeys<T, keyof T>]: T[key] }; // Make all properties (keys) of an object required
export type RequiredKeyAndValue<T, K extends keyof T> = T & { [P in K]-?: NonNullable<T[P]> }; // Make a specific key and its value required

export interface StringIndexedDict<T = any> {
  [property: string]: T;
}
export interface IntIndexedDict<T = any> {
  [property: number]: T;
}
// ###########################################################################################################
// Entity building blocks
export type IdTypes = string | number;
export type IdType<T extends IdTypes> = T;

export interface GenericEntity {
  id: IdTypes;
}
export interface IGenericEntity<T extends IdTypes = number> {
  id: IdType<T>;
}

export type GenericIdEntity = IGenericEntity<GenericEntity["id"]>;

export interface INamedEntity {
  name: string;
}

export interface IPermissionedEntity {
  permissions?: IEntityPermissions;
}
export interface IEntityPermissions {
  canEdit?: boolean;
  canTrash?: boolean;
  canRestore?: boolean;
  canDelete?: boolean;
  canLock?: boolean;
  canUnlock?: boolean;
  canSign?: boolean;
}
export interface IRelation {
  count: number;
  link?: string;
}
export interface IRelationModel {
  [property: string]: IRelation;
}
export type InferRelations<T> = T extends IRelationModel
  ? { [K in keyof T as string extends K ? never : K]: T[K] }
  : never;

export type IModelWithIntId = IGenericEntity<number>;
export type IModelWithStringId = IGenericEntity<string>;
export type IConfigFile = IGenericEntity<string>;

export interface IEntityMinimalModel<T extends IdTypes = number> extends ISoftDeletable, ILockable, ISignable {
  id: IdType<T>;
  name: string;
  version?: number;
  uid?: string;
  link?: string;
}
export interface TypedEntitySuggestionsModel<T extends IdTypes = number> extends IEntityMinimalModel<T> {
  customType: IEntityMinimalModel<number> | null;
}
export interface IProjectBasedResponseModel {
  projects: IEntityMinimalModel[];
}
export interface IOwnedEntity {
  owner: IEntityMinimalModel<number> | null;
}
export interface IRelatedEntity<T extends IRelationModel> {
  relations?: InferRelations<T>;
}
// Deprecated
// export interface ICustomSchemaEntity {
//   type: IEntityMinimalModel<string> | null;
// }
// Deprecated
// export interface ICustomFieldsEntity {
//   customFields?: StringIndexedDict;
// }

export interface ICustomTypedEntity {
  customType: IEntityMinimalModel<CustomType["id"]> | null;
  customFields?: StringIndexedDict; // This a purely synthetic frontend property that is generated on the fly
  customValues?: CustomFieldMapModel[];
}

export interface IHierarchyTypeRead {
  inventoryName?: string | null;
  isHierarchyRoot?: boolean | null;
  rootHierarchy?: IEntityMinimalModel<CustomType["id"]> | null;
  parentTypes?: IEntityMinimalModel<CustomType["id"]>[] | null;
}

export interface CustomFieldMapModel {
  id?: number;
  name: string;
  type: CustomFieldMapModelType;
  content?: CustomFieldMapModel[];
  dataType?: CustomFieldDataType;
  value?: any;
}

type CustomFieldMapModelType = "CustomField" | "CustomTypeSection";

export interface IEnteredOn {
  enteredOn: string;
}
export interface IEnteredBy {
  enteredBy: IEntityMinimalModel;
}
export type IEntryRecord = IEnteredOn & IEnteredBy;

export interface IModifiedOn {
  modifiedOn: string;
}
export interface IModifiedBy {
  modifiedBy: IEntityMinimalModel;
}
export type IModificationRecord = IModifiedOn & IModifiedBy;
export interface IVersionedEntity {
  version: number;
}
export const isIVersionedEntity = (entity?: object): entity is IVersionedEntity => {
  if (!entity) return false;
  return "version" in entity;
};

export interface IVersionModel {
  originalId: number;
  version: number;
}
export interface IConcurrencyToken<T> {
  concurrencyToken?: T | null;
}
export interface ISoftDeletable {
  isDeleted?: boolean;
}
export interface IUniqueEntity<T extends IdTypes = number> {
  uid: string;
  origin?: IEntityMinimalModel<T>;
  importExportEvents?: ImportExportEventRead[];
}

interface ImportExportEventRead {
  foreignStorage?: IEntityMinimalModel<number>;
  timestamp?: string;
  eventType?: "Import" | "Export";
  foreignIdentifier?: string;
  foreignUid?: string;
  foreignUrl?: string;
  foreignInformation?: any;
}

export interface IHierarchyRead {
  ancestors: IEntityMinimalModel[];
  parent: IEntityMinimalModel;
}

export interface ISessionedEntity extends IModifiedOn {
  currentlyEditedBy?: Person;
  currentlyEditedSince?: string;
  publicSessionId?: string;
  isCurrentlyEdited: boolean;
}

// Lockable entities

export interface ILockable {
  isLocked?: boolean;
}

export interface ILockableEntity extends ILockable {
  lockedBy?: Person;
  lockedOn?: string;
}

// Signable entities
export interface ISignable {
  isSigned?: boolean;
}

export interface ISignableEntity extends ISignable {
  signatures?: SignatureSuggestions[];
}

// ###########################################################################################################
// API response types
export interface IResultModel<T> {
  results: T[];
  count?: number;
}
export interface IPagination<T> extends IResultModel<T> {
  hasNext: boolean;
}

export interface IBulkPermissionResponse<T extends IdTypes = number> extends Required<IPermissionedEntity> {
  id: IdType<T>;
}

// ###########################################################################################################
// API query filter interfaces
export const GenericEntityOrderBy = ["ID"] as const;
export type IGenericEntityOrderBy = (typeof GenericEntityOrderBy)[number];

export const NamedEntityOrderBy = ["NAME"] as const;
export type INamedEntityOrderBy = (typeof NamedEntityOrderBy)[number];

export const OwnedOrderBy = ["OWNER"] as const;
export type IOwnedOrderBy = (typeof OwnedOrderBy)[number];

export const TypedEntityOrderBy = ["CUSTOM_TYPE", "CUSTOM_FIELD"] as const;
export type ITypedEntityOrderBy = (typeof TypedEntityOrderBy)[number];

export const EntryRecordOrderBy = ["ENTERED_ON", "ENTERED_BY"] as const;
export type IEntryRecordOrderBy = (typeof EntryRecordOrderBy)[number];

export const ModificationRecordOrderBy = ["MODIFIED_ON", "MODIFIED_BY"] as const;
export type IModificationRecordOrderBy = (typeof ModificationRecordOrderBy)[number];

export const VersionedOrderBy = ["VERSION"] as const;
export type IVersionedOrderBy = (typeof VersionedOrderBy)[number];

export const DefaultHierarchyOrderTypeConsts = ["NUMBER_OF_ITEMS", "INVENTORY_NAME", "LAYOUT"] as const;
export type DefaultHierarchyOrderType = (typeof DefaultHierarchyOrderTypeConsts)[number];

export interface IPaginationParameters {
  page?: number;
  pageSize?: number;
}

export interface IOwnedEntityParameters {
  ownerIds?: number[] | null;
}

export interface INamedEntityParameters {
  names?: string[] | null;
}
export interface IProjectBasedParameters {
  projectIds?: number[] | null;
}
export interface ISearchTermParameters {
  searchTerm?: string | null;
  searchTermIncludeIds?: boolean | null;
  searchTermIncludeUuids?: boolean | null;
  searchTermIncludeNames?: boolean | null;
  searchTermIncludeCustomFieldValues?: boolean | null;
}

export const ISearchTermParametersDefaults: ISearchTermParameters = {
  searchTerm: "",
  searchTermIncludeNames: true,
  searchTermIncludeIds: true,
  searchTermIncludeUuids: true,
  searchTermIncludeCustomFieldValues: true,
} as const;

export interface IFilterParameter<IdTypes> extends ISearchTermParameters {
  ids?: IdTypes[] | null;
  excludeIds?: IdTypes[];
}

export type SortDirection = "ASC" | "DESC";
export interface EntityOrderBy<OrderType = ""> {
  property?: OrderType | null;
  sortDirection?: SortDirection;
  customFieldId?: number;
}

export interface IListRequestParameters<OrderType = ""> {
  // orderBy?: DefaultOrderType | OrderType | null;
  sortBy?: EntityOrderBy<OrderType>[] | null;
  includeCount?: boolean;
}
export interface IGenericRequestParameters<Entity extends GenericEntity, OrderType = "">
  extends IFilterParameter<Entity["id"]>, IListRequestParameters<OrderType> {
  name?: string | null; // Not sure how to implement this one, as we cannot derive it from inheritage of INamedEntity
}

export type InferSortByOptions<T> = T extends IListRequestParameters<infer OrderType> ? OrderType : never;

export interface ITypedEntityParameters extends ICustomFieldSearch {
  customFieldIds?: number[] | null;
  customTypeIds?: number[] | null;
}

export const CustomFieldValuesSearchPredicateConst = ["AND", "OR"] as const;
export type CustomFieldValuesSearchPredicate = (typeof CustomFieldValuesSearchPredicateConst)[number];

export const CustomFieldSearchOperatorConst = [
  "EQUALS",
  "NOT_EQUALS",
  "GREATER_THAN",
  "GREATER_THAN_OR_EQUAL",
  "LESS_THAN",
  "LESS_THAN_OR_EQUAL",
  "STRING_CONTAINS",
  "STRING_NOT_CONTAINS",
  "IN",
  "NOT_IN",
  "STARTS_WITH",
  "ENDS_WITH",
  "IS_NULL",
  "IS_NOT_NULL",
] as const;
export type CustomFieldSearchOperator = (typeof CustomFieldSearchOperatorConst)[number];

export interface ICustomFieldSearch {
  filterQuery?: CustomFieldSearchQuery | null;
}

export interface CustomFieldSearchQuery {
  predicate?: CustomFieldValuesSearchPredicate;
  left?: CustomFieldSearchQuery;
  right?: CustomFieldSearchQuery;
  customFieldId?: number;
  operator?: CustomFieldSearchOperator;
  value?: any;
}

export interface ICreatedRecordParameters {
  enteredFrom?: string | null;
  enteredTo?: string | null;
  enteredByIds?: number[] | null;
}

export interface IModifiedRecordParameters {
  modifiedFrom?: string | null;
  modifiedTo?: string | null;
  modifiedByIds?: number[] | null;
}

export interface IVersionedEntityParameters {
  original_ids?: number[];
  verions?: number[];
}

export interface ISoftDeletableFilterParameters {
  includeSoftDeleted?: boolean | null;
  isSoftDeleted?: boolean | null;
}

export interface IHierarchyParameters {
  childrenOfParentIds?: number[] | null;
  descendantsOfIds?: number[] | null;
  excludeHierarchyChildren?: boolean | null;
  isHierarchyRoot?: boolean | null;
  inventoryIds?: number[] | null;
}

export interface IUniqueEntityParameters {
  originIds?: number[] | null;
}

export interface ISessionedEntityStartSessionParameters extends ISessionedEntityStopSessionParameters {
  forceTakeOver: boolean;
  connectionId: string;
}

export interface ISessionedEntityStopSessionParameters {
  publicSessionId: string;
}

export interface ILockableEntityParameters {
  lockedByIds?: number[] | null;
  lockedFrom?: string | null;
  lockedTo?: string | null;
  isLocked?: boolean | null;
}

export interface ISignableEntityParameters {
  signedByIds?: number[] | null;
  notSignedByIds?: number[] | null;
  signedFrom?: string | null;
  signedTo?: string | null;
  signatureTypes?: SignatureType[] | null;
  isSigned?: boolean | null;
}

// ###########################################################################################################
// Pure frontend interfaces
export interface EntityConstantsBase {
  entitySingular: string;
  entityPlural: string;
  icon: IconNames;
}

// We use the generic type to allow for type inference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface EntityConstants<Entity extends any = {}, Filters extends any = {}> extends EntityConstantsBase {
  resource: ResourceName;
  frontendIndexRoute: RouteKey;
}
